import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {BalansMVA, BokkzVpbDataService, CorporateTaxEntity,} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe, DatePipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-andere-vaste-activa',
  templateUrl: './spec-andere-vaste-activa.component.html',
  styleUrls: ['./spec-andere-vaste-activa.component.scss'],
  imports: [
    SharedUiButtonModule,
    SharedUiDialogModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    AsyncPipe,
    DatePipe
  ]
})
export class SpecAndereVasteActivaComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public dialogVisible = false;

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public andereVasteActivaFg = new FormGroup({
    omschrijving: new FormControl<string | null>(null),
    openingsbalans: new FormControl<number | null>(null),
    slotbalans: new FormControl<number | null>(null),
    nominaleWaardeOfKostenprijs: new FormControl<number | null>(null, {
      nonNullable: true,
      validators: [
        Validators.required,
      ],
    }),
    restwaarde: new FormControl<number | null>(null, {
      nonNullable: true,
      validators: [
        Validators.required,
      ],
    })
  })

  showDialog() {
    this.dialogVisible = true;
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      if (data.aangifte?.balansEnResultatenrekening?.andereVasteActiva?.at(0)) {
        this.andereVasteActivaFg.patchValue(
          data.aangifte.balansEnResultatenrekening.andereVasteActiva[0],
          {emitEvent: false}
        );
      }
    });

    this.andereVasteActivaFg.valueChanges.pipe(
      debounceTime(500),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.andereVasteActivaFg.updateValueAndValidity({emitEvent: false});

    const formValue = this.andereVasteActivaFg.getRawValue();
    const specificatie: BalansMVA = {
      omschrijving: formValue.omschrijving?.trim() || undefined,
      openingsbalans: formValue.openingsbalans ?? undefined,
      slotbalans: formValue.slotbalans ?? undefined,
      nominaleWaardeOfKostenprijs: formValue.nominaleWaardeOfKostenprijs ?? undefined,
      restwaarde: formValue.restwaarde ?? undefined,
    };

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const huidigeSpecificatie = currentEntity.aangifte.balansEnResultatenrekening?.andereVasteActiva?.[0];

      const mergedSpecificatie: BalansMVA = {
        ...huidigeSpecificatie,
        ...specificatie,
        // Preserve the main balance values when only the detailed specification is edited.
        openingsbalans: specificatie.openingsbalans ?? huidigeSpecificatie?.openingsbalans,
        slotbalans: specificatie.slotbalans ?? huidigeSpecificatie?.slotbalans,
      };

      const hasData = Object.values(mergedSpecificatie).some(value => value !== undefined);

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening: {
            ...currentEntity.aangifte.balansEnResultatenrekening,
            andereVasteActiva: hasData ? [mergedSpecificatie] : undefined,
          },
        },
      } as CorporateTaxEntity;
    });
  }
}
